// The workspace: header, rail, detail. Everything stateful lives in the store
// or in the pane that owns it; this file is layout and the shortcuts.

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Snackbar,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import ReorderIcon from '@mui/icons-material/Reorder';
import HealingIcon from '@mui/icons-material/Healing';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import VideoOrderModal from '../components/VideoOrderModal';
import { useAdmin } from './AdminStore';
import VideoRail from './VideoRail';
import VideoDetail from './VideoDetail';
import NewVideoDialog from './NewVideoDialog';
import RepairDialog from './RepairDialog';
import BulkFinishDialog, { readyUnmarked } from './BulkFinishDialog';
import { deriveStatus } from './schema';

export function AdminWorkspace() {
  const {
    videos,
    selectedId,
    select,
    loading,
    refresh,
    logout,
    notification,
    dismissToast,
    pendingSelect,
    confirmPendingSelect,
    cancelPendingSelect,
    activeUploads,
  } = useAdmin();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [newVideo, setNewVideo] = useState({ open: false, duplicateOf: null });
  const [orderOpen, setOrderOpen] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);
  const [bulkFinishOpen, setBulkFinishOpen] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // A reload kills an upload in flight and leaves a half-written file on
  // api.video that no record will ever point at. The browser will only show its
  // own wording here, but the prompt is the part that matters.
  useEffect(() => {
    if (!activeUploads.length) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [activeUploads.length]);

  // `/` focuses search from anywhere that is not already a text field.
  useEffect(() => {
    const onKey = (event) => {
      const tag = event.target.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || event.target.isContentEditable;
      if (event.key === '/' && !typing) {
        event.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
      if (event.key === 'Escape' && document.activeElement === searchRef.current) {
        searchRef.current.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const todo = Object.values(videos).filter((v) => !deriveStatus(v).finished).length;
  const seedable = readyUnmarked(videos).length;

  return (
    <div className="admin-root">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          px: '10px',
          py: '6px',
          borderBottom: '1px solid var(--ps-line)',
          background: 'linear-gradient(var(--ps-surface-top), var(--ps-bg-panel))',
        }}
      >
        <span className="admin-label" style={{ fontSize: 12, color: 'var(--ps-gold)', marginRight: 12 }}>
          THE_VIDEOS
        </span>

        <Button startIcon={<AddIcon sx={{ fontSize: 15 }} />} onClick={() => setNewVideo({ open: true, duplicateOf: null })}>
          New
        </Button>
        <Button startIcon={<ReorderIcon sx={{ fontSize: 15 }} />} onClick={() => setOrderOpen(true)}>
          Order
        </Button>
        <Tooltip title="Reload the catalog">
          <Button onClick={refresh} disabled={loading}>
            <RefreshIcon sx={{ fontSize: 15 }} />
          </Button>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        {/* An upload survives the tab it was started from, so the only place
            that can honestly report one is the shell. Without this, walking
            away from the Media tab looked exactly like the upload dying. */}
        {activeUploads.map((upload) => (
          <Tooltip
            key={`${upload.identifier}:${upload.kind}`}
            describeChild
            title={`${upload.filename} → ${upload.identifier}. Leaving the page cancels it.`}
          >
            <span
              className="admin-label"
              style={{ marginRight: 8, color: 'var(--ps-gold)' }}
            >
              {upload.kind} upload {upload.state === 'registering' ? 'registering' : `${upload.progress}%`}
            </span>
          </Tooltip>
        ))}

        {todo > 0 && (
          <span className="admin-label" style={{ marginRight: 8 }}>
            {todo} to do
          </span>
        )}

        {/* Self-hiding: once the backlog is seeded this never renders again,
            so it doesn't become permanent chrome.

            describeChild, here and on Repair: without it MUI makes the tooltip
            the button's accessible name, hiding the visible label from screen
            readers. Icon-only buttons keep the default, where the tooltip is
            the only name they have. */}
        {seedable > 0 && (
          <Tooltip describeChild title="These pass every check but have never been marked finished">
            <Button
              startIcon={<DoneAllIcon sx={{ fontSize: 15 }} />}
              onClick={() => setBulkFinishOpen(true)}
              sx={{ borderColor: 'var(--ps-gold)', color: 'var(--ps-gold)' }}
            >
              Mark {seedable} finished
            </Button>
          </Tooltip>
        )}
        <Tooltip describeChild title="Scan for records the site cannot read correctly">
          <Button startIcon={<HealingIcon sx={{ fontSize: 15 }} />} onClick={() => setRepairOpen(true)}>
            Repair
          </Button>
        </Tooltip>
        <Button variant="text" startIcon={<LogoutIcon sx={{ fontSize: 15 }} />} onClick={logout}>
          Log out
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ height: 2, borderRadius: 0, border: 0 }} />}

      <div className="admin-body" data-detail-open={selectedId ? 'true' : 'false'}>
        <VideoRail
          search={search}
          onSearch={setSearch}
          filter={filter}
          onFilter={setFilter}
          searchRef={searchRef}
        />
        <VideoDetail
          identifier={selectedId}
          onBack={() => select(null)}
          onDuplicate={(id) => setNewVideo({ open: true, duplicateOf: id })}
        />
      </div>

      <NewVideoDialog
        open={newVideo.open}
        duplicateOf={newVideo.duplicateOf}
        onClose={() => setNewVideo({ open: false, duplicateOf: null })}
        onCreated={(id) => {
          setNewVideo({ open: false, duplicateOf: null });
          // A brand new record has nothing to lose, so it jumps the guard.
          select(id, { force: true });
        }}
      />

      {/* The edit buffer is not saved as you type, so moving away from it has
          to ask. The old admin lost the field you were editing without a word. */}
      <Dialog open={!!pendingSelect} onClose={cancelPendingSelect}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            The Overview tab has edits that have not been saved. Leaving now discards them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPendingSelect}>Keep editing</Button>
          <Button variant="contained" color="error" onClick={confirmPendingSelect}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <VideoOrderModal isOpen={orderOpen} onClose={() => setOrderOpen(false)} />
      <RepairDialog open={repairOpen} onClose={() => setRepairOpen(false)} />
      <BulkFinishDialog open={bulkFinishOpen} onClose={() => setBulkFinishOpen(false)} />

      {/* The one notification channel. Three components used to own their own,
          all anchored bottom-center, so they stacked on top of each other. */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={dismissToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={dismissToast}
          severity={notification.severity}
          sx={{ minWidth: 280, boxShadow: '0 8px 28px rgba(0,0,0,0.55)' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default AdminWorkspace;
